import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    GoogleMap,
    useJsApiLoader,
    Marker,
    InfoWindow,
    Polyline,
    TransitLayer,
} from "@react-google-maps/api";
import { toast } from "react-toastify";
import { getData, deleteDataById } from "../../../Services/Services";
import { getroutname, deleteRoute, getRouteDetails } from "../../../Services/EndPoints";
import LoadingOverlay from "./LoadingOverlay";
import SearchableRouteDropdown from "./SearchableRouteDropdown";
import "./RouteList.css";
import "./RouteEnhancements.css";
import { useNavigate } from "react-router-dom";
import useModulePermissions, { FLEET_PERMISSION_IDS } from "../Common/hooks/useModulePermissions";

// ─── Shared Google Maps loader (fixes "Loader must not be called again" error) ─
import {
    GOOGLE_MAPS_API_KEY,
    GOOGLE_MAPS_LIBRARIES,
    GOOGLE_MAPS_LOADER_ID,
} from "../../../../src/Pages/DMS/GpsTrackManagement/Googlemapsloader";

// ─── Map config ───────────────────────────────────────────────────────────────
const MAP_CONTAINER_STYLE = { height: "600px", width: "100%" };
const DEFAULT_CENTER = { lat: 17.385044, lng: 78.486671 };
const MAP_OPTIONS = {
    mapTypeControl: false,
    streetViewControl: true,
    fullscreenControl: true,
    zoomControl: true,
    clickableIcons: false,
    gestureHandling: 'greedy',
};
const mapTypes = {
    road: "roadmap",
    satellite: "satellite",
    terrain: "terrain",
    hybrid: "hybrid",
};

// ─── Suppress Google InfoWindow native close button ──────────────────────────
const infoWindowStyle = `
  .gm-ui-hover-effect { display: none !important; }
  .gm-style-iw-chr   { display: none !important; }
  .gm-style-iw-d     { overflow: hidden !important; padding: 0 !important; }
  .gm-style-iw-c     { padding: 0 !important; border-radius: 8px !important;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.2) !important; }
  .gm-style-iw       { padding: 0 !important; border-radius: 8px !important; }
`;

// ─── Layer options — inline SVGs (no lucide-react needed) ────────────────────
const LAYERS = [
    {
        id: "road", label: "Standard",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
    },
    {
        id: "satellite", label: "Satellite",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    },
    {
        id: "terrain", label: "Terrain",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 18 9 6 13 13 16 9 21 18"/></svg>,
    },
    {
        id: "hybrid", label: "Hybrid",
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
    },
];

// ─── Inline Layers button icon ────────────────────────────────────────────────
const LayersIcon = ({ color = "#475569" }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 2 7 12 12 22 7 12 2"/>
        <polyline points="2 17 12 22 22 17"/>
        <polyline points="2 12 12 17 22 12"/>
    </svg>
);

// ─── SVG icons ────────────────────────────────────────────────────────────────
const makePinSvg = (fillColor) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="44" viewBox="0 0 28 44">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 5.817 3.412 10.83 8.35 13.269L14 44l5.65-16.731C24.588 24.83 28 19.817 28 14 28 6.268 21.732 0 14 0z"
      fill="${fillColor}" stroke="white" stroke-width="1.5"/>
    <circle cx="14" cy="14" r="5.5" fill="white" fill-opacity="0.85"/>
  </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const makeHaltSvg = () => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
    <circle cx="17" cy="17" r="16" fill="#FBBC04" stroke="white" stroke-width="2.5"/>
    <rect x="11" y="10" width="4" height="14" rx="1.5" fill="white"/>
    <rect x="19" y="10" width="4" height="14" rx="1.5" fill="white"/>
  </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const createCustomIcon = (type) => {
    if (!window.google) return undefined;
    if (type === "halt") {
        return {
            url: makeHaltSvg(),
            scaledSize: new window.google.maps.Size(34, 34),
            anchor: new window.google.maps.Point(17, 17),
        };
    }
    const pinColor = type === "start" ? "#34A853" : "#EA4335";
    return {
        url: makePinSvg(pinColor),
        scaledSize: new window.google.maps.Size(28, 44),
        anchor: new window.google.maps.Point(14, 44),
    };
};

// ─── Component ────────────────────────────────────────────────────────────────
const RouteList = () => {
    // Role's Edit / Delete rights for Create/Assign Route (web_permissions from getUserProfile)
    const { canEdit, canDelete } = useModulePermissions(FLEET_PERMISSION_IDS.CREATE_ASSIGN_ROUTE);
    const { isLoaded } = useJsApiLoader({
        id: GOOGLE_MAPS_LOADER_ID,
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [routes, setRoutes] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [routePoints, setRoutePoints] = useState([]);
    const [snappedRouteCoordinates, setSnappedRouteCoordinates] = useState([]);
    const [routePointsEdit, setRoutePointsEdit] = useState([]);
    const [activeMarkerId, setActiveMarkerId] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ show: false, route: null });
    const [selectedLayer, setSelectedLayer] = useState("road");
    const [showLayerControl, setShowLayerControl] = useState(false);
    const [showTransit, setShowTransit] = useState(false);

    const mapRef = useRef(null);
    const geocoderRef = useRef(null);
    const autocompleteRef = useRef(null);
    const searchTimerRef = useRef(null);
    const navigate = useNavigate();

    const [searchQuery, setSearchQuery]   = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching]   = useState(false);

    // ── Init Google Geocoder once map loads ───────────────────────────────────
    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
        geocoderRef.current = new window.google.maps.Geocoder();
        autocompleteRef.current = new window.google.maps.places.AutocompleteService();
        map.setOptions({
            mapTypeControlOptions: {
                style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                position: window.google.maps.ControlPosition.TOP_RIGHT,
                mapTypeIds: [
                    window.google.maps.MapTypeId.ROADMAP,
                    window.google.maps.MapTypeId.SATELLITE,
                    window.google.maps.MapTypeId.TERRAIN,
                    window.google.maps.MapTypeId.HYBRID,
                ],
            },
        });
    }, []);

    // ── fitBounds ─────────────────────────────────────────────────────────────
    const fitMapToPoints = useCallback((points) => {
        if (!mapRef.current || !points.length) return;
        const bounds = new window.google.maps.LatLngBounds();
        points.forEach((p) =>
            bounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
        );
        mapRef.current.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
    }, []);

    // ── Map search ────────────────────────────────────────────────────────────
    const searchLocation = useCallback((query) => {
        if (!query.trim()) { setSearchResults([]); return; }
        if (!autocompleteRef.current) return;
        setIsSearching(true);
        autocompleteRef.current.getPlacePredictions(
            { input: query, componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] },
            (predictions, status) => {
                setIsSearching(false);
                const OK = window.google.maps.places.PlacesServiceStatus.OK;
                if (status === OK && predictions?.length) {
                    setSearchResults(predictions.map((p) => ({ id: p.place_id, name: p.description, placeId: p.place_id })));
                } else { setSearchResults([]); }
            }
        );
    }, []);

    const selectSearchResult = useCallback((result) => {
        setSearchQuery(result.name);
        setSearchResults([]);
        if (!geocoderRef.current) return;
        geocoderRef.current.geocode({ placeId: result.placeId }, (results, status) => {
            if (status === "OK" && results[0]) {
                const loc = results[0].geometry.location;
                if (mapRef.current) {
                    if (results[0].geometry.viewport) mapRef.current.fitBounds(results[0].geometry.viewport);
                    else { mapRef.current.setCenter({ lat: loc.lat(), lng: loc.lng() }); mapRef.current.setZoom(15); }
                }
                toast.success(`Map centred on: ${result.name.split(",")[0]}`);
            }
        });
    }, []);

    const handleSearchInputChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => searchLocation(query), 500);
    };

    const clearSearch = () => {
        setSearchQuery("");
        setSearchResults([]);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };

    // ── Reverse geocode ───────────────────────────────────────────────────────
    const reverseGeocode = useCallback((lat, lng) => {
        return new Promise((resolve) => {
            if (!geocoderRef.current) { resolve(null); return; }
            geocoderRef.current.geocode(
                { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
                (results, status) => {
                    if (status === "OK" && results[0]) {
                        const components = results[0].address_components;
                        const get = (...types) => {
                            for (const type of types) {
                                const c = components.find((ac) => ac.types.includes(type));
                                if (c) return c.long_name;
                            }
                            return null;
                        };
                        const road = get("route", "premise", "establishment");
                        const area = get("sublocality_level_1", "sublocality", "neighborhood");
                        const city = get("locality", "administrative_area_level_3");
                        const state = get("administrative_area_level_1");
                        const pincode = get("postal_code");
                        const parts = [road, area, city, state, pincode].filter(Boolean);
                        resolve(parts.length ? parts.join(", ") : results[0].formatted_address);
                    } else {
                        resolve(null);
                    }
                }
            );
        });
    }, []);

    // ── Geocode key points in background ──────────────────────────────────────
    const geocodeKeyPoints = useCallback(
        async (keyPoints) => {
            try {
                const promises = keyPoints.map(async (point, i) => {
                    await new Promise((r) => setTimeout(r, i * 300));
                    const address = await reverseGeocode(parseFloat(point.latitude), parseFloat(point.longitude));
                    return {
                        ...point,
                        address: address || `${parseFloat(point.latitude).toFixed(6)}, ${parseFloat(point.longitude).toFixed(6)}`,
                        isLoadingAddress: false,
                    };
                });
                const results = await Promise.allSettled(promises);
                const geocoded = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
                setRoutePoints(geocoded);
            } catch {
                toast.warning("Some addresses failed to load", { autoClose: 2000 });
            }
        },
        [reverseGeocode]
    );

    // ── Fetch route list ──────────────────────────────────────────────────────
    const fetchRoutes = async () => {
        try {
            setIsLoading(true);
            const response = await getData(getroutname);
            if (response) setRoutes(response);
        } catch {
            toast.error("Failed to fetch routes");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchRoutes(); }, []);

    // ── Select a route ────────────────────────────────────────────────────────
    const handleRouteSelect = async (e) => {
        const routeId = e.target.value;
        if (!routeId) {
            setSelectedRoute(null);
            setRoutePoints([]);
            setSnappedRouteCoordinates([]);
            if (mapRef.current) {
                mapRef.current.setCenter(DEFAULT_CENTER);
                mapRef.current.setZoom(12);
            }
            return;
        }

        const route = routes.find((r) => r.routeId === parseInt(routeId));
        setSelectedRoute(route);

        try {
            setIsLoading(true);
            const response = await getData(getRouteDetails.replace("ID", routeId));
            if (response?.data?.waypoints) {
                const sorted = response.data.waypoints.sort((a, b) => a.sequenceNo - b.sequenceNo);
                setRoutePointsEdit(response.data.waypoints);

                const allCoords = sorted.map((p) => [parseFloat(p.latitude), parseFloat(p.longitude)]);
                setSnappedRouteCoordinates(allCoords);

                const keyPoints = sorted.filter((p) => {
                    const t = p.pointType?.toLowerCase();
                    return t === "start" || t === "halt" || t === "end";
                });

                const withPlaceholders = keyPoints.map((p) => ({
                    ...p,
                    address: `${parseFloat(p.latitude).toFixed(6)}, ${parseFloat(p.longitude).toFixed(6)}`,
                    isLoadingAddress: true,
                }));
                setRoutePoints(withPlaceholders);

                setTimeout(() => fitMapToPoints(sorted), 150);
                if (isLoaded) geocodeKeyPoints(keyPoints);
            }
        } catch {
            toast.error("Failed to fetch route details");
            setRoutePoints([]);
            setSnappedRouteCoordinates([]);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Edit ──────────────────────────────────────────────────────────────────
    const handleEditClick = (route) => {
        navigate("/RoutePlanning/exptected-route", {
            state: { editRoute: true, routeData: route, routePoints: routePointsEdit },
        });
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDeleteClick = (route) => setDeleteModal({ show: true, route });
    const handleCancelDelete = () => setDeleteModal({ show: false, route: null });

    const handleConfirmDelete = async () => {
        const { route } = deleteModal;
        if (!route) return;
        try {
            setIsLoading(true);
            const response = await deleteDataById(deleteRoute, route.routeId);
            if (response?.status === "success") {
                toast.success(response.msg || "Route deleted successfully", { position: "top-center" });
                if (selectedRoute?.routeId === route.routeId) {
                    setSelectedRoute(null);
                    setRoutePoints([]);
                    setSnappedRouteCoordinates([]);
                }
                await fetchRoutes();
            } else {
                toast.error(response?.msg || "Failed to delete route", { position: "top-center" });
            }
        } catch {
            toast.error("An error occurred while deleting. Please try again.", { position: "top-center" });
        } finally {
            setIsLoading(false);
            setDeleteModal({ show: false, route: null });
        }
    };

    // ── Polyline path ─────────────────────────────────────────────────────────
    const polylinePath =
        snappedRouteCoordinates.length > 1
            ? snappedRouteCoordinates.map(([lat, lng]) => ({ lat, lng }))
            : routePoints.length >= 2
                ? [...routePoints]
                    .sort((a, b) => a.sequenceNo - b.sequenceNo)
                    .map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }))
                : [];

    let haltIndex = 0;

    const keyPoints = routePoints.filter((p) => {
        const t = p.pointType?.toLowerCase();
        return t === "start" || t === "halt" || t === "end";
    }).sort((a, b) => a.sequenceNo - b.sequenceNo);

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="row g-3" style={{ marginTop: 0, alignItems: "stretch" }}>
            <style>{infoWindowStyle}</style>

            {/* ── Left Panel ───────────────────────────────────────────────────── */}
            <div className="col-md-4 col-lg-4" style={{ marginTop: 0, display: "flex", flexDirection: "column" }}>
                <div className="card border-0" style={{ flex: 1, display: "flex", flexDirection: "column", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", borderRadius: 12, overflow: "hidden" }}>
                    {/* Brand gradient header */}
                    <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "5px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                                <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                            </svg>
                        </div>
                        <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>Route List</span>
                    </div>

                    <div className="card-body" style={{ padding: 16, flex: 1, display: "flex", flexDirection: "column" }}>
                        {/* Route Selector */}
                        <div style={{ marginBottom: 14 }}>
                            <SearchableRouteDropdown
                                routes={routes}
                                selectedRoute={selectedRoute}
                                setSelectedRoute={(route) =>
                                    handleRouteSelect({ target: { value: route?.routeId || "" } })
                                }
                                isLoading={isLoading}
                            />
                        </div>

                        {/* Route Details Card */}
                        {selectedRoute && (
                            <div className="route-detail-card" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                                {/* Brand accent bar */}
                                <div style={{ height: 3, background: "linear-gradient(90deg, #0f4a45 0%, #1a7a73 100%)" }} />

                                {/* Route name header */}
                                <div style={{ padding: "11px 14px 10px" }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#94a3b8", marginBottom: 3 }}>Route Details</div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", lineHeight: 1.3 }}>{selectedRoute.routeName}</div>
                                </div>

                                {/* Divider */}
                                <div style={{ height: 1, background: "#f1f5f9", margin: "0 14px" }} />

                                {/* From → To visual connector */}
                                <div style={{ padding: "12px 14px" }}>
                                    <div style={{ display: "flex", alignItems: "stretch", gap: 0 }}>
                                        {/* Dot + line + dot */}
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 18, flexShrink: 0, paddingTop: 3 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 0 2.5px rgba(34,197,94,0.25)", flexShrink: 0 }} />
                                            <div style={{ width: 2, flex: 1, minHeight: 18, background: "linear-gradient(180deg, #22c55e 0%, #ef4444 100%)", margin: "3px 0", borderRadius: 2 }} />
                                            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 0 2.5px rgba(239,68,68,0.25)", flexShrink: 0 }} />
                                        </div>

                                        {/* Labels */}
                                        <div style={{ flex: 1, paddingLeft: 10 }}>
                                            <div style={{ marginBottom: 14 }}>
                                                <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#16a34a", marginBottom: 2 }}>From</div>
                                                <div style={{ fontSize: 12, color: "#374151", fontWeight: 500, lineHeight: 1.4 }}>{selectedRoute.fromLocation}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#dc2626", marginBottom: 2 }}>To</div>
                                                <div style={{ fontSize: 12, color: "#374151", fontWeight: 500, lineHeight: 1.4 }}>{selectedRoute.toLocation}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {(canEdit || canDelete) && <>
                                {/* Divider */}
                                <div style={{ height: 1, background: "#f1f5f9" }} />

                                {/* Action buttons (shown per role's Edit / Delete permission) */}
                                <div style={{ padding: "10px 14px", display: "flex", gap: 8 }}>
                                    {canEdit && <button
                                        title="Edit Route"
                                        className="btn-route-primary"
                                        onClick={() => handleEditClick(selectedRoute)}
                                        style={{ flex: 1, padding: "9px 0", borderRadius: 7, border: "none", background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, boxShadow: "0 2px 6px rgba(26,122,115,0.25)" }}
                                    >
                                        <i className="fas fa-edit" style={{ fontSize: 11 }} />
                                        Edit Route
                                    </button>}
                                    {canDelete && <button
                                        title="Delete Route"
                                        className="btn-route-danger"
                                        onClick={() => handleDeleteClick(selectedRoute)}
                                        disabled={isLoading}
                                        style={{ flex: 1, padding: "9px 0", borderRadius: 7, border: "none", background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: isLoading ? "default" : "pointer", fontSize: 12, fontWeight: 600, boxShadow: "0 2px 6px rgba(239,68,68,0.25)", opacity: isLoading ? 0.6 : 1 }}
                                    >
                                        <i className="fas fa-trash" style={{ fontSize: 11 }} />
                                        Delete
                                    </button>}
                                </div>
                                </>}
                            </div>
                        )}

                        {/* Route Points List */}
                        {selectedRoute && keyPoints.length > 0 && (
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#64748b", marginBottom: 8 }}>
                                    Route Points ({keyPoints.length})
                                </div>
                                <div className="route-scrollbar" style={{ maxHeight: 290, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, paddingRight: 2 }}>
                                    {keyPoints.map((point, idx) => {
                                        const t = point.pointType?.toLowerCase();
                                        const cfgMap = {
                                            start: { c: "#22c55e", tint: "#f0fdf4", dark: "#16a34a", label: "Start" },
                                            halt: { c: "#f59e0b", tint: "#fffbeb", dark: "#d97706", label: "Halt" },
                                            end: { c: "#ef4444", tint: "#fef2f2", dark: "#dc2626", label: "End" },
                                        };
                                        const cfg = cfgMap[t] || { c: "#64748b", tint: "#f8fafc", dark: "#475569", label: "Way" };
                                        return (
                                            <div key={point.id} style={{ background: cfg.tint, borderLeft: `3px solid ${cfg.c}`, borderRadius: 8, padding: "8px 10px" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                                                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: cfg.c, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                        {idx + 1}
                                                    </div>
                                                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: cfg.dark, letterSpacing: "0.3px", flex: 1 }}>
                                                        {cfg.label} Point
                                                    </span>
                                                    {t === "halt" && (
                                                        <span style={{ fontSize: 10, fontWeight: 600, color: "#d97706", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 10, padding: "1px 7px", flexShrink: 0 }}>
                                                            {point.waitTime}min
                                                        </span>
                                                    )}
                                                </div>
                                                {point.isLoadingAddress ? (
                                                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#94a3b8", marginLeft: 29 }}>
                                                        <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10, borderWidth: 1.5 }} />
                                                        Loading address...
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.4, marginLeft: 29, wordBreak: "break-word" }}>
                                                        {point.address || `${parseFloat(point.latitude).toFixed(6)}, ${parseFloat(point.longitude).toFixed(6)}`}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Empty State */}
                        {!selectedRoute && (
                            <div style={{ textAlign: "center", padding: "40px 20px" }}>
                                <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                                    <i className="fas fa-map-marked-alt" style={{ fontSize: 26, color: "#cbd5e1" }} />
                                </div>
                                <div style={{ fontSize: 14, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>No Route Selected</div>
                                <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>Select a route from the dropdown above to view its details and map visualization</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Map Panel ────────────────────────────────────────────────────── */}
            <div className="col-md-8" style={{ position: "relative", zIndex: 1, marginTop: 0, display: "flex", flexDirection: "column" }}>
                <div className="card border-0 route-map-card" style={{ flex: 1, boxShadow: "0 2px 16px rgba(0,0,0,0.09)", borderRadius: 12, overflow: "hidden" }}>
                    {/* Brand gradient header */}
                    <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "5px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                            </svg>
                        </div>
                        <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, flex: 1 }}>
                            {selectedRoute ? `Map: ${selectedRoute.routeName}` : "Route Map"}
                        </span>
                        <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                            {(LAYERS.find(l => l.id === selectedLayer)?.label ?? selectedLayer)} layer
                        </span>
                    </div>

                    {/* Search bar */}
                    <div style={{ padding: "10px 14px 0", position: "relative" }}>
                        <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                            <div style={{ padding: "0 10px", color: "#94a3b8", display: "flex", alignItems: "center" }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            </div>
                            <input type="text" placeholder="Search city, area, landmark or coordinates…"
                                value={searchQuery} onChange={handleSearchInputChange}
                                style={{ flex: 1, border: "none", outline: "none", fontSize: 13, padding: "8px 4px", color: "#1e293b", background: "transparent" }} />
                            {searchQuery && (
                                <button type="button" onClick={clearSearch}
                                    style={{ padding: "0 10px", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, display: "flex", alignItems: "center" }}>✕</button>
                            )}
                            {isSearching && (
                                <div style={{ padding: "0 10px" }}>
                                    <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 14, height: 14, borderWidth: 2 }} />
                                </div>
                            )}
                        </div>
                        {searchResults.length > 0 && (
                            <div style={{ position: "absolute", left: 14, right: 14, top: "calc(100% - 2px)", zIndex: 1050, background: "#fff", borderRadius: "0 0 10px 10px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0", borderTop: "none", overflow: "hidden" }}>
                                {searchResults.map((result) => (
                                    <button key={result.id} type="button" onClick={() => selectSearchResult(result)}
                                        className="search-result-row"
                                        style={{ width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "flex-start", gap: 10 }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{result.name.split(",")[0]}</div>
                                            <div style={{ fontSize: 11, color: "#64748b" }}>{result.name.split(",").slice(1, 3).join(",")}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="card-body p-0 position-relative">
                        {isLoading && <LoadingOverlay message="Loading route data..." />}

                        {isLoaded ? (
                            <GoogleMap
                                mapContainerStyle={MAP_CONTAINER_STYLE}
                                center={DEFAULT_CENTER}
                                zoom={12}
                                options={{ ...MAP_OPTIONS, mapTypeId: mapTypes[selectedLayer], minZoom: 4 }}
                                onLoad={onMapLoad}
                            >
                                {polylinePath.length > 1 && (
                                    <Polyline path={polylinePath} options={{ strokeColor: "#1a7a73", strokeWeight: 4, strokeOpacity: 0.75 }} />
                                )}

                                {selectedRoute && routePoints.length > 0 && routePoints.map((point) => {
                                    const isHalt = point.pointType?.toLowerCase() === "halt";
                                    const currentHaltIdx = isHalt ? haltIndex++ : null;
                                    const type = point.pointType?.toLowerCase();
                                    return (
                                        <Marker
                                            key={point.id}
                                            position={{ lat: parseFloat(point.latitude), lng: parseFloat(point.longitude) }}
                                            icon={createCustomIcon(type)}
                                            onClick={() => setActiveMarkerId(point.id)}
                                        >
                                            {activeMarkerId === point.id && (() => {
                                                const ptCfgMap = {
                                                    start: { color: "#22c55e", dark: "#16a34a", tint: "#f0fdf4", label: "Start Point", sub: "Planned starting location" },
                                                    halt: { color: "#f59e0b", dark: "#d97706", tint: "#fffbeb", label: `Halt Point ${currentHaltIdx + 1}`, sub: "Scheduled stop location" },
                                                    end: { color: "#ef4444", dark: "#dc2626", tint: "#fef2f2", label: "End Point", sub: "Planned ending location" },
                                                };
                                                const ptCfg = ptCfgMap[type] || { color: "#64748b", dark: "#475569", tint: "#f8fafc", label: "Way Point", sub: "" };
                                                return (
                                                    <InfoWindow onCloseClick={() => setActiveMarkerId(null)} options={{ maxWidth: 300 }}>
                                                        <div style={{ minWidth: 260, maxWidth: 300, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
                                                            {/* Gradient header */}
                                                            <div style={{ background: `linear-gradient(135deg, ${ptCfg.color} 0%, ${ptCfg.dark} 100%)`, padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                                                                <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                                    {type === "halt" ? (
                                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16" rx="2"/><rect x="14" y="4" width="4" height="16" rx="2"/></svg>
                                                                    ) : (
                                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg>
                                                                    )}
                                                                </div>
                                                                <div style={{ flex: 1 }}>
                                                                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{ptCfg.label}</div>
                                                                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>{ptCfg.sub}</div>
                                                                </div>
                                                                <button onClick={() => setActiveMarkerId(null)} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
                                                            </div>
                                                            {/* Body */}
                                                            <div style={{ padding: "12px 14px 14px", background: "#fff" }}>
                                                                {type === "halt" && (
                                                                    <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
                                                                        <div style={{ width: 26, height: 26, borderRadius: 6, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                                        </div>
                                                                        <div>
                                                                            <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Wait Duration</div>
                                                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                                                <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{point.waitTime}</div>
                                                                                <div style={{ fontSize: 10, color: "#64748b", background: "#fef3c7", borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>mins</div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                                                                    <div style={{ width: 26, height: 26, borderRadius: 6, background: ptCfg.tint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={ptCfg.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                                                    </div>
                                                                    <div style={{ flex: 1 }}>
                                                                        <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Location</div>
                                                                        <div style={{ fontSize: 11, color: "#475569", background: ptCfg.tint, borderRadius: 6, padding: "6px 10px", borderLeft: `3px solid ${ptCfg.color}`, lineHeight: 1.5 }}>
                                                                            {point.isLoadingAddress ? (
                                                                                <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Loading address…</span>
                                                                            ) : (
                                                                                point.address || `${parseFloat(point.latitude).toFixed(6)}, ${parseFloat(point.longitude).toFixed(6)}`
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </InfoWindow>
                                                );
                                            })()}
                                        </Marker>
                                    );
                                })}

                                {showTransit && <TransitLayer />}

                                {/* ── Map Controls Column ── */}
                                <div style={{ position: "absolute", top: 60, right: 10, zIndex: 1000, display: "flex", flexDirection: "column", gap: 8 }}>
                                    {/* Layer Switcher */}
                                    <div style={{ position: "relative" }}>
                                        <button
                                            onClick={() => setShowLayerControl((p) => !p)}
                                            title="Switch map layer"
                                            style={{ width: 40, height: 40, borderRadius: 10, background: showLayerControl ? "#1a7a73" : "#fff", border: `1px solid ${showLayerControl ? "#1a7a73" : "#e2e8f0"}`, boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                        >
                                            <LayersIcon color={showLayerControl ? "white" : "#475569"} />
                                        </button>
                                        {showLayerControl && (
                                            <div style={{ position: "absolute", top: 46, right: 0, background: "#fff", borderRadius: 10, padding: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.14)", minWidth: 150, border: "1px solid #e2e8f0" }}>
                                                {LAYERS.map((l) => (
                                                    <div key={l.id} onClick={() => { setSelectedLayer(l.id); setShowLayerControl(false); }}
                                                        style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 10px", borderRadius: 7, cursor: "pointer", background: selectedLayer === l.id ? "#f0fdfb" : "transparent", transition: "background 0.15s" }}>
                                                        <span style={{ color: selectedLayer === l.id ? "#1a7a73" : "#94a3b8" }}>{l.icon}</span>
                                                        <span style={{ fontSize: 13, fontWeight: selectedLayer === l.id ? 600 : 400, color: selectedLayer === l.id ? "#1a7a73" : "#334155" }}>{l.label}</span>
                                                        {selectedLayer === l.id && <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "#1a7a73" }} />}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Transit Layer Toggle */}
                                    <button
                                        onClick={() => setShowTransit((p) => !p)}
                                        title={showTransit ? "Hide transit" : "Show transit"}
                                        style={{ width: 40, height: 40, borderRadius: 10, background: showTransit ? "#1a7a73" : "#fff", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showTransit ? "#fff" : "#555"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="4" y="2" width="16" height="16" rx="2"/>
                                            <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
                                            <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
                                        </svg>
                                    </button>

                                    {/* Zoom to Route */}
                                    {routePoints.length > 0 && (
                                        <button
                                            onClick={() => fitMapToPoints(routePoints)}
                                            title="Zoom to route"
                                            style={{ width: 40, height: 40, borderRadius: 10, background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
                                                <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </GoogleMap>
                        ) : (
                            <div style={MAP_CONTAINER_STYLE} className="d-flex align-items-center justify-content-center bg-light">
                                <div className="spinner-border text-info" role="status"><span className="visually-hidden">Loading map…</span></div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
            {deleteModal.show && deleteModal.route && (
                <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(3px)", zIndex: 9999 }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
                        <div className="modal-content route-modal-content" style={{ border: "none", borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,0.28)" }}>
                            {/* Red gradient header */}
                            <div style={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", padding: "16px 18px", display: "flex", alignItems: "center", gap: 12 }}>
                                <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                                    </svg>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Delete Route</div>
                                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>This action cannot be undone</div>
                                </div>
                                <button
                                    onClick={handleCancelDelete}
                                    disabled={isLoading}
                                    style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 700, padding: 0, flexShrink: 0 }}
                                >✕</button>
                            </div>

                            <div style={{ padding: "16px 18px 0" }}>
                                {/* Route info card */}
                                <div style={{ background: "#fef2f2", borderLeft: "4px solid #ef4444", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#ef4444", marginBottom: 6 }}>Route to Delete</div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>{deleteModal.route.routeName}</div>
                                    <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#64748b", flexWrap: "wrap" }}>
                                        <span><strong style={{ color: "#374151" }}>From:</strong> {deleteModal.route.fromLocation}</span>
                                        <span><strong style={{ color: "#374151" }}>To:</strong> {deleteModal.route.toLocation}</span>
                                    </div>
                                </div>

                                {/* Warning banner */}
                                <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "9px 12px", display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 16 }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                                    </svg>
                                    <span style={{ fontSize: 12, color: "#92400e", lineHeight: 1.5 }}>All route points and mapping data will be permanently removed.</span>
                                </div>
                            </div>

                            {/* Footer */}
                            <div style={{ padding: "0 18px 18px", display: "flex", gap: 10 }}>
                                <button
                                    className="btn-route-secondary"
                                    onClick={handleCancelDelete}
                                    disabled={isLoading}
                                    style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
                                >
                                    Discard
                                </button>
                                <button
                                    className="btn-route-danger"
                                    onClick={handleConfirmDelete}
                                    disabled={isLoading}
                                    style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "none", background: isLoading ? "#fca5a5" : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: isLoading ? "default" : "pointer" }}
                                >
                                    {isLoading ? (
                                        <><span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }} />Deleting...</>
                                    ) : "Confirm Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RouteList;
